export const ETAPAS_QUALIDADE_INBOX = {
  'ATIVAÇÃO DE CHIP': 'eSIM/Chip',
  'BOAS-VINDAS': 'BV',
  'PRIMEIRA FATURA': '1 Fatura',
} as const;

export function contarClientesQualidade(fila: Array<{ documento: string; etapas: string[] }>) {
  const etapas: Record<string, Set<string>> = {};
  for (const conversa of fila) for (const etapa of conversa.etapas) {
    (etapas[`qualidade:${etapa}`] ??= new Set()).add(conversa.documento);
  }
  return Object.fromEntries(Object.entries(etapas).map(([etapa, clientes]) => [etapa, clientes.size]));
}

export function etapaQualidadeTab(tab: string): string | null {
  const etapa = tab.startsWith('qualidade:') ? tab.slice(10) : '';
  return Object.prototype.hasOwnProperty.call(ETAPAS_QUALIDADE_INBOX, etapa) ? etapa : null;
}

export interface MomentoQualidade {
  etapa: string;
  competencia: string | null;
  /** Semáforo da verificação de eSIM: nao_iniciado · em_andamento · validado.
   *  Só a etapa do chip tem; nas outras é null. */
  estado: string | null;
}
export interface ConversaFilaQualidade { documento: string; etapas: string[]; momentos?: MomentoQualidade[] }

/**
 * Os clientes de uma etapa, opcionalmente recortados por COMPETÊNCIA (Douglas
 * 22/09/2026: "posso selecionar um tipo de ciclo, depois abaixo a competência").
 * Roda sobre o dicionário que já está em cache — nenhuma consulta nova, e
 * filtrar ENCOLHE a lista de documentos que a tela vai buscar.
 */
export function documentosDaEtapa(fila: ConversaFilaQualidade[], etapa: string,
  competencias: string[] = [], estados: string[] = []) {
  const alvoComp = competencias.filter(Boolean);
  const alvoEstado = estados.filter(Boolean);
  return fila.filter(row => {
    if (!row.etapas.includes(etapa)) return false;
    if (!alvoComp.length && !alvoEstado.length) return true;
    // O momento inteiro: etapa + competência + estado juntos. O cliente pode
    // estar em eSIM de agosto e em BV de setembro; cruzar listas soltas
    // acusaria um eSIM de setembro que não existe (Douglas 22/09/2026).
    return (row.momentos || []).some(m => m.etapa === etapa
      && (!alvoComp.length || alvoComp.includes(m.competencia || 'sem-competencia'))
      && (!alvoEstado.length || alvoEstado.includes(m.estado || '')));
  }).map(row => row.documento);
}

/** Estados da verificação presentes na etapa, com quantos clientes em cada um. */
export function estadosDaEtapa(fila: ConversaFilaQualidade[] | undefined, etapa: string) {
  const contagem = new Map<string, number>();
  for (const row of fila || []) {
    for (const m of row.momentos || []) {
      if (m.etapa !== etapa || !m.estado) continue;
      contagem.set(m.estado, (contagem.get(m.estado) || 0) + 1);
    }
  }
  const ordem = ['nao_iniciado', 'em_andamento', 'validado'];
  return ordem.filter(e => contagem.has(e)).map(valor => ({ valor, total: contagem.get(valor)! }));
}

/** Competências disponíveis na etapa, com quantos clientes cada uma tem. */
export function competenciasDaEtapa(fila: ConversaFilaQualidade[] | undefined, etapa: string) {
  const contagem = new Map<string, number>();
  for (const row of fila || []) {
    for (const m of row.momentos || []) {
      if (m.etapa !== etapa) continue;
      const chave = m.competencia || 'sem-competencia';
      contagem.set(chave, (contagem.get(chave) || 0) + 1);
    }
  }
  return [...contagem].sort((a, b) => a[0] < b[0] ? -1 : 1).map(([valor, total]) => ({ valor, total }));
}
